import React, { useMemo, useState } from "react";
import ZoomableDiagram from "./index";
import { splitPlantumlByFlow } from "./splitPuml";

// Renders a large PlantUML diagram as a stacked/accordion list of per-flow
// sub-diagrams (plus a "Full diagram" panel). Each panel lazily mounts its own
// ZoomableDiagram only when expanded, so we don't fire N render calls at once.
export default function SplitDiagramView({ plantumlCode, plantumlEncoder }) {
  const sections = useMemo(() => splitPlantumlByFlow(plantumlCode), [plantumlCode]);

  // Panels: a "Full diagram" panel first, then one per flow. Track open state by key.
  const panels = useMemo(() => ([
    { key: "__full__", title: "📄 Full diagram", plantuml: plantumlCode, full: true },
    ...sections.map((s, i) => ({ key: `flow-${i}`, title: s.title, plantuml: s.plantuml })),
  ]), [sections, plantumlCode]);

  // First flow panel open by default (index 1); Full collapsed.
  const [open, setOpen] = useState(() => ({ [panels[1]?.key || panels[0].key]: true }));
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <div style={{ fontSize: 11, color: "#7a93bc", padding: "2px 2px 0" }}>
        Split into {sections.length} flow{sections.length !== 1 ? "s" : ""} for readability — each renders on its own.
      </div>
      {panels.map((p) => {
        const isOpen = !!open[p.key];
        const lineCount = (p.plantuml || "").split("\n").length;
        return (
          <div key={p.key} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", background: "#0d1117" }}>
            <button
              onClick={() => toggle(p.key)}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "9px 12px", background: p.full ? "rgba(59,127,255,0.06)" : "#161b22",
                border: "none", borderBottom: isOpen ? "1px solid rgba(255,255,255,0.08)" : "none",
                color: "#dce8ff", fontSize: 12.5, fontWeight: 600, cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 10, color: "#6a7a90" }}>{isOpen ? "▾" : "▸"}</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
              <span style={{ fontSize: 10.5, color: "#5a6b80", fontFamily: "monospace" }}>{lineCount} lines</span>
            </button>
            {isOpen && (
              <div style={{ padding: 8 }}>
                {/* Per-flow panels are already small — suppress the "diagram is large"
                    warning there; keep it only on the "Full diagram" panel. */}
                <ZoomableDiagram plantumlCode={p.plantuml} plantumlEncoder={plantumlEncoder} hideLargeWarning={!p.full} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
